import { Box, Card, CardHeader, darken, Grid, lighten, Skeleton, Stack, Typography, emphasize, useTheme, Tooltip, Divider, Chip, Link } from "@mui/material";
import { addDays, addYears, eachDayOfInterval } from "date-fns";
import { ListContextProvider, ReduxState, useGetMainList, useGetOne, useListController } from "ra-core";
import { Link as RouteLink, useLocation } from 'react-router-dom'
import Chart from 'react-apexcharts';
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import numeral from "numeral";
import { format as formatDfn } from "date-fns";
import { useSnackbar } from "notistack";
import { Content } from "@/components/content/Content";
import { PopTimeRangeKind, usePopTimeRangePicker } from "@/components/input/PopTimeRangePicker";
import { DmScanReportSummaryDto, DmScanReportSummaryStatisDto, ReportSummaryKind } from "@/gen/api/securefile";
import { OdResourcePageProps } from "@/types/resource";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { PrimaryClassification, SecondaryClassificationResponseDto } from "@/gen/api/governance";
import { RecPatch } from "@/dataProvider/tools";
import { SimpleOption } from "@/components/governance/search/AmbientFacilitiesContext";
import { Pagination as DefaultPagination } from '@/components/list/pagination';
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import Scrollbar from "@/components/Scrollbar";
import { crudGetManyEx } from "@/actions/crudFetchEx";
import { ClientNameDto } from "@/gen/api/user";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import { BackToTopButton } from "../layout/Layout";
import OnDataLock from "@/icons/OnDataLock";
import DateField2 from "@/components/field/DateField2";
import { Datagrid } from "@/components/list/datagrid";
import { FunctionField } from "@/components/field";
import { PopBaseFilter } from "@/components/input/PopAutocompleteFilter";
import { push } from "connected-react-router";
import { getUserProfile } from "@/reducers/userProfile";
import { base64UrlEncode } from "@/utils/base64";



const statisChartsRes = 'securefile-dm-reports-statis-charts'
const statisTableRes = 'securefile-dm-reports-statis-table'
const dmClientNameRes = 'securefile-dm-client-names'

const DataManagerReports: FC<OdResourcePageProps> = (props) => {
  useResourceRegister({ name: statisChartsRes })
  useResourceRegister({ name: statisTableRes })
  useResourceRegister({ name: dmClientNameRes })

  const registered = useSelector<ReduxState>(store => {
    return !!store.admin.resources[statisChartsRes]
  })

  const userProfile = useSelector(getUserProfile)

  if (!registered || !userProfile) { return null }

  return (
    <DataManagerReportsBody {...props} ianaTimeZone={userProfile.ianaTimeZone ?? null} />
  )
}

function initialS(s: PassbackRefFilesParams | undefined, ianaTimeZone: string|null) {
  return s
    ? {
      notBefore: s?.notBefore ?? (addDays(new Date(), -7)).toISOString(),
      notAfter: s?.notAfter ?? new Date().toISOString(),
      trKind: s?.trKind ?? PopTimeRangeKind.Days_7,
      table: s.clients?.length
        ? {
          clientNameMap: s.clients.reduce((acc, x, i) => {
            if (s.clientNames && s.clientNames[i]) {
              acc[x] = s.clientNames[i];
            }
            return acc;
          }, {} as any),
          clients: new Set<string>(s.clients),
          selectedClients: [...s.clients],
        }
        : {
          clientNameMap: {} as Record<string, string>,
          clients: new Set<string>(),
          selectedClients: [] as string[],
        },
      totalReportCount: null as number | null,
      statisId: 
        (s.notAfter || s.notBefore)
        ? JSON.stringify({ notBefore: s.notBefore, notAfter: s.notAfter, clients: s.clients, ianaTimeZone })
        : '',
    }
    : {
      notBefore: (addDays(new Date(), -7)).toISOString(),
      notAfter: new Date().toISOString(),
      trKind: PopTimeRangeKind.Days_7,
      table: {
        clientNameMap: {} as Record<string, string>,
        clients: new Set<string>(),
        selectedClients: [] as string[],
      },
      totalReportCount: null as number | null,
      statisId: '',
    };
}

const DataManagerReportsBody: FC<OdResourcePageProps&{ianaTimeZone:string|null}> = ({
  options, resource, ianaTimeZone,
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const sRef = useRef(location.state as PassbackRefFilesParams|undefined)
  useEffect(() => {
    if (sRef.current) {
      if (!location.state) {
        sRef.current = undefined
      } else {
        location.state = undefined
        dispatch(push(location))
      }
    }
  }, [dispatch, location])

  const [states, setStates] = useState(() => initialS(sRef.current, ianaTimeZone))

  const { enqueueSnackbar } = useSnackbar()

  const {setSearchTime} = useMemo(() => ({
    setSearchTime: (start: Date|null, end: Date|null, kind: PopTimeRangeKind, reason: string) => {
      setStates(v => {
        const notBefore = (start ?? addDays(new Date(), -7)).toISOString() as any
        const notAfter = (end ?? new Date()).toISOString()  as any
        if (v.notAfter === notAfter && v.notBefore === notBefore) { return v }
        const v1 = {...v,
          notBefore,
          notAfter,
          trKind: kind,
          ...(reason !== 'init' && {
            table: {
              clientNameMap: v.table.clientNameMap,
              clients: new Set<string>(),
              selectedClients: [] as string[],
            }
          } as any),
          totalReportCount: null,
          statisId: 
            reason === 'init' && v.table.selectedClients.length > 0
            ? JSON.stringify({ notBefore, notAfter, clients: v.table.selectedClients, ianaTimeZone })
            : JSON.stringify({ notBefore, notAfter, ianaTimeZone }),
        }
        return v1
      })
    },
  }), [ianaTimeZone])

  const chartStatisOnFailure = (err: any) => {
    const errMsg = retrieveAxiosErr(err)
    enqueueSnackbar(`Error occurred, please refresh the page later: ${errMsg}`, { variant: 'error' })
  }
  
  const { current: hints } = useRef({ hintBook: [] as string[], key: '', total: 0, pageSize: 0 })
  const k = JSON.stringify([states.notAfter as any, states.notBefore, states.table.selectedClients.length])
  if (k !== hints.key) {
    hints.hintBook.length = 0
    hints.key = k
    hints.total = 0
    hints.pageSize = 0
  }
  const listCtrl = useListController({ resource: statisTableRes, basePath: '', perPage: 25,
    filter: { notBefore: states.notBefore, notAfter: states.notAfter, clients: states.table.selectedClients, getHint: () => ({ hintBook: hints.hintBook, total: hints.total, pageSize: hints.pageSize })} })

  useEffect(() => {
    if (listCtrl.loaded) {
      const pageEnd: string|undefined = (listCtrl.ids ?? []).flatMap(id => {
        const dto = listCtrl.data[id] as DmScanReportSummaryDto|undefined
        return dto?.endTime ? [dto?.endTime] : []
      }).sort()[0]
      if (pageEnd) {
        hints.hintBook[listCtrl.page] = pageEnd
      }
      hints.total = listCtrl.total
    }
    hints.pageSize = listCtrl.perPage
  }, [hints, listCtrl.data, listCtrl.ids, listCtrl.loaded, listCtrl.page, listCtrl.perPage, listCtrl.total])

  const format = useUserTimeFormat()

  const { data: statisDto, loaded } = useGetOne(statisChartsRes, states.statisId, { onFailure: chartStatisOnFailure })

  const startEndRef = useRef(null as string|null)
  useEffect(() => {
    if (loaded && statisDto) {
      setStates(v => {
        const v1 = {...v, totalReportCount: statisDto.grandTotal?.summariesCount ?? 0}
        const anchor = JSON.stringify({ start: v.notBefore, end: v.notAfter })
        if (startEndRef.current !== anchor) {
          const ls : string[] = statisDto.activeClients ?? []
          const lsSet = new Set(ls)
          if (v.table.clients.size === ls.length && v.table.clients.difference(lsSet).size === 0) {
            return v1;
          }
          const unknowns = ls.filter(x => !v.table.clientNameMap[x])
          if (unknowns.length > 0) {
            dispatch(crudGetManyEx(dmClientNameRes, [...unknowns], {
              onSuccess: ({data}) => {
                const clientNames : ClientNameDto[] = data
                setStates(v2 => {
                  for (const cname of clientNames) {
                    v2.table.clientNameMap[cname.clientId!] = cname.clientName!
                  }
                  return ({...v2})
                })
              }
            }))
          }
          v1.table.clients = lsSet
          startEndRef.current = anchor
        }
        return v1
      })
    }
  }, [dispatch, loaded, statisDto])

  return (
    <Content pageName="Data Manager Scan Reports" resourceName="Data Manager Scan Reports" breadcrumbs={options?.breadcrumbs}>
      {states.table.selectedClients.length === 0 && states.table.clients.size > 0 &&
        <Stack direction="row-reverse" sx={{ mt: -2, mb: 1 }}>
          <PopClientFilter states={states} setStates={setStates as any} ianaTimeZone={ianaTimeZone} />
        </Stack>}
      <Grid container spacing={4} >
        {states.table.selectedClients.length > 0 &&
          <Grid item xs={12} container>
            <Card sx={{ borderRadius: 0.5, width: '100%' }}>
              <Stack direction="row" sx={{ px: 2 }}>
                <Typography variant="body1" marginRight="auto" sx={{ lineHeight: '32px',  }}>Filters Applied</Typography>
                <PopClientFilter states={states} setStates={setStates as any} ianaTimeZone={ianaTimeZone} />
              </Stack>
              <Divider />
                <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ px: 2, py: 0.5 }}>
                  {states.table.selectedClients.map((c,i) => 
                    <Chip key={c} sx={{ m: 1 }} variant="outlined"
                      label={
                        <Stack direction="row" alignItems="center" sx={{ '& span': {fontWeight: 600}}} >
                          <span>client</span>
                          : {states.table.clientNameMap[c]}
                        </Stack>
                      }
                      onDelete={() => setStates(v => {
                        if (v.table.selectedClients[i] !== c) { return v}
                        v.table.selectedClients.splice(i, 1)
                        return {...v,
                          statisId: JSON.stringify({ notBefore: v.notBefore, notAfter: v.notAfter, clients: v.table.selectedClients, ianaTimeZone }),
                        }
                      })}
                    />
                  )}
                </Stack>
            </Card>
          </Grid>
        }

        <StatisChart statisDto={statisDto as any} clientsInfo={states.table} setSearchTime={setSearchTime} 
          initCustom={sRef.current ? {
            trKind: sRef.current.trKind,
            initStart: new Date(sRef.current.notBefore),
            initEnd: new Date(sRef.current.notAfter),
          } : undefined}
        />

        <Grid item xs={12}>
          <ListContextProvider value={listCtrl}>
            <Card>
              <CardHeader title="Reports" />
              <Divider />
              <Scrollbar options={{ suppressScrollY: true }}>
                <Box sx={{ minWidth: 1150 }}>
                  <Datagrid>
                    <DateField2 source="endTime" label="Timestamp" showTime sortable={false} />
                    <FunctionField source="clientId" label="Client" sortable={false}
                      render={(d0) => {
                        if (!d0) { return null }
                        const d: DmScanReportSummaryDto = d0 as any
                        const machine = states.table.clientNameMap[d!.clientId!]
                        const refparams = { notBefore: states.notBefore, notAfter: states.notAfter, kind: 0, 
                          trKind: states.trKind,
                          clients: states.table.selectedClients,
                          clientNames: states.table.selectedClients?.map(x => states.table.clientNameMap[x])
                        };
                        return (
                          <Tooltip title={`View ${machine} Scan Info`}>
                            <Link component={RouteLink} underline="hover"
                              to={{
                                pathname: `/secure-files/clients/${d0.clientId}/dm-scan-info`,
                                search: `machine=${base64UrlEncode(machine)}&stateParams=${base64UrlEncode(JSON.stringify(refparams))}`
                              }}
                            >
                              <Typography variant="body2">{machine}</Typography>
                            </Link>
                          </Tooltip>
                        )
                      }} 
                      />
                    <FunctionField source="client" label="Scanned / Classified" sortable={false}
                      render={(d0) => {
                        if (!d0) { return null }
                        const d: DmScanReportSummaryDto = d0 as any
                        const clientName = states.table.clientNameMap[d.clientId!]
                        const refparams = { notBefore: states.notBefore, notAfter: states.notAfter, kind: 0, inReport: d.id,
                          timerangeDesc: `${clientName} / ${format(d.endTime ?? 0, 'PPp')}`,
                          trKind: states.trKind,
                          clients: states.table.selectedClients,
                          clientNames: states.table.selectedClients?.map(x => states.table.clientNameMap[x])
                        }
                        return (
                          <Typography variant="body2" sx={{ position: 'relative', '& > svg': { position: 'absolute', left: -24, top: 2 } }}>
                            {d.kind === ReportSummaryKind.EncryptedFile &&
                              <OnDataLock transform="scale(0.8)" fontSize="small" color="disabled" />}
                            <Tooltip title={d.kind === ReportSummaryKind.EncryptedFile ? 'Encrypted File Scan' : 'Plain File Scan'}>
                              <RouteLink 
                                to={{ 
                                  pathname: '/secure-files/data-manager-reports/file-refs', 
                                  search: `RefFilesParams=${base64UrlEncode(JSON.stringify(refparams))}`
                                }}
                              >
                                <Typography color="primary" component="span">{commFormatter(d.entryCount ?? 0)}</Typography> 
                              </RouteLink>
                            </Tooltip>
                            {" / "}
                            <Typography color="success" component="span">{commFormatter(Object.values(d.classificationStatisBag?.primaryClassification ?? {}).reduce((a, b) => a + b, 0))}</Typography>
                          </Typography>
                        )
                      }} 
                    />
                  </Datagrid>
                </Box>
              </Scrollbar>
              {//@ts-ignore
                <DefaultPagination {...listCtrl} rowsPerPageOptions={[25, 50, 100]} />}
            </Card>
          </ListContextProvider>
          <BackToTopButton />
        </Grid>
      </Grid>
    </Content>
  )
}

export default DataManagerReports;


const StatisChart : FC<{ 
  statisDto: DmScanReportSummaryStatisDto|undefined,
  clientsInfo?: {
    clientNameMap: any;
    clients: Set<string>;
    selectedClients: string[];
  }, 
  setSearchTime: (start: Date|null, end: Date|null, kind: PopTimeRangeKind, reason: string)=>void, 
  initCustom?: { trKind: PopTimeRangeKind, initStart?: Date|null, initEnd?: Date|null },
}> = ({
  statisDto, setSearchTime, clientsInfo, initCustom,
}) => {
  const [states, setStates] = useState({
    classificationSelection: PrimaryClassification.Confidential,
    sndClssMap: {} as Record<string, {text:string[],count:number,cid:number}[]>,
    timerangeCustom: { startBoundary: null as Date|null, endBoundary: new Date() }
  })
  const dispatch = useDispatch()
  const format = useUserTimeFormat()
  const theme = useTheme()
  const userProfile = useSelector(getUserProfile)

  const { PopTimeRangePicker, open, states: tStates } = usePopTimeRangePicker({
    label: '',
    defaultKind: initCustom?.trKind ?? PopTimeRangeKind.Days_7,
    onChange: ({kind, start, end}, adjust, reason) => {
      if (!start && !end) { return }
      else if (!start && end) {
        const start1 = addYears(end, -1)
        setSearchTime(start1, end, kind, reason!)
        adjust(start1, end)
        return 
      }
      
      const start1 = start!
      const now = new Date()
      let endBoundary = addYears(start1, 1)
      const limitRange = endBoundary < now
      endBoundary = limitRange ? endBoundary : now
      const end1 = end ? (endBoundary < end ? endBoundary : end) : (limitRange ? endBoundary : null)
      
      setSearchTime(start1, end1, kind, reason!)
      if (end1 && (!end || end1 < end)) {
        adjust(start1, end1)
      }
    },
    PopoverProps: {
      anchorOrigin: { vertical: 'top', horizontal: 'left' },
      transformOrigin: { vertical: 'top', horizontal: 'left' },
    },
    custom: {...states.timerangeCustom, initStart: initCustom?.initStart, initEnd: initCustom?.initEnd},
    ianaTimeZone: userProfile?.ianaTimeZone,
    // excludesOptions: [PopTimeRangeKind.Hours_24]
  })
  const { data: data2, ids: ids2 } = useGetMainList<SecondaryClassificationResponseDto&RecPatch>('settings/governance/data-classification', pages(), sorts(), {})

  const {classifications} = useMemo(() => ({
    classifications: (ids2 ?? [])
      .map((id) => {
        const c = data2![id as number]
        const s : SimpleOption = c as any
        s.key = c.secondaryClassificationId!
        s.text = `${c.primaryClassificationText} - ${c.name}`
        return s as any
      })
      .sort((a, b) => 
            ((a.primaryClassification || 0) - (b.primaryClassification || 0)) 
        || ((a.secondaryClassificationId || 0) - (b.secondaryClassificationId || 0))) as SecondaryClassificationResponseDto[],
  }), [data2, ids2])

  const {chartOptions: docTypeChartOptions, chartHeight} = useMemo(() => {
    const [categories, data] : [string[], number[]] =
      Object.entries(statisDto?.documentTypeDistribution ?? {}).sort((a, b) => a[1] === b[1] ? (a[0] < b[0] ? -1 : 1) : a[1] - b[1] < 0 ? 1 : -1)
        .reduce((acc, x) => {
          if (acc[0].length > 15) { return acc } // at most 16 data points
          const maxCount = acc[1][0] ?? 0
          if (x[1] < (maxCount / 40)) { return acc } // proportion too small to render
          acc[0].push(x[0])
          acc[1].push(x[1])
          return acc
        }, [[], []] as any)
    const chartOptions = {
      series: [{ name: 'file count', data }],
      options: {
        chart: { toolbar: { show: false }, },
        plotOptions: {
          bar: {
            borderRadius: 4,
            borderRadiusApplication: 'end',
            horizontal: true,
          }
        },
        dataLabels: { enabled: true, formatter: commFormatter },
        xaxis: { categories, 
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { show: false },
        },
        yaxis: { labels: { minWidth: 80 } },
        grid: { show: false },
        tooltip: {
          y: { formatter: commFormatter }
        },
        theme: { mode: theme.palette.mode }
      },
    };
    const chartHeight = Math.floor(Math.max(data.length * (275 / 8), 275))
    return {chartOptions, chartHeight}
  }, [statisDto?.documentTypeDistribution, theme.palette.mode])

  const clssPieChartOptions = useMemo(() => {
    const [labels, series] : [string[], number[]] = Object.entries(statisDto?.primaryClssDistribution ?? {}).sort((a, b) => a[1] - b[1] < 0 ? 1 : -1)
      .reduce((acc, x) => {
        acc[0].push(x[0])
        acc[1].push(x[1])
        return acc
      }, [[], []] as any)
    return ({
      series,
      options: {
        chart: { width: 180, 
          events: {
            dataPointSelection: (e: any, c: any, selection: any) => {
              setStates(v => {
                const selectedClss : any = PrimaryClassification[labels[selection.dataPointIndex as number] as any]
                return v.classificationSelection === selectedClss ? v : {...v, classificationSelection: selectedClss }
              })
            },
          },
        },
        labels,
        tooltip: { y: { formatter: commFormatter, show: true } },
        theme: { mode: theme.palette.mode }
      },
    })
  }, [statisDto?.primaryClssDistribution, theme.palette.mode])

  useEffect(() => {
    const sndClssMap = Object.entries(statisDto?.secondaryClssDistribution ?? {}).reduce((acc, x) => {
      const cid = +x[0]
      const clss = classifications.find(c => c.secondaryClassificationId === cid)
      if (clss) {
        const arr = acc[clss.primaryClassification!] ?? (acc[clss.primaryClassification!] = [])
        arr.push({
          text: [PrimaryClassification[clss.primaryClassification!], clss.name!],
          count: x[1],
          cid
        })
      }
      return acc
    }, {} as Record<string, {text:string[],count:number,cid:number}[]>)
    for (const arr of Object.values(sndClssMap)) {
      arr.sort((a, b) => a.count - b.count < 0 ? 1 : -1)
    }
    const keys = Object.keys(sndClssMap).sort()
    setStates(v => ({...v, sndClssMap,
      ...(keys.length === 0 || sndClssMap[v.classificationSelection] ? {} 
        : {classificationSelection: +keys[0]})
    }))
  }, [classifications, statisDto?.secondaryClssDistribution])
  
  const sndClssChartOptions = useMemo(() => {
    const arr = states.sndClssMap[states.classificationSelection as any]
    if (!arr) { return null }
    const categories = arr.map(x => x.text)
    const data = arr.map(x => x.count)
    const sndClssChartOptions = {
      series: [{ name: 'file count', data, }],
      options: {
        chart: { height: 200, toolbar: { show: false }, 
          events: {
            dataPointSelection: (e: any, c: any, selection: any) => {
              const clssx = arr[selection.dataPointIndex]
              const refparams = { notBefore: tStates.start, notAfter: tStates.end ?? new Date(), 
                classifiedAsSClss: clssx.cid,
                timerangeDesc: clssx.text.join(' - '),
                trKind: tStates.kind,
                clients: clientsInfo?.selectedClients,
                clientNames: clientsInfo?.selectedClients?.map(x => clientsInfo.clientNameMap[x])
              };
              dispatch(push({ 
                pathname: '/secure-files/data-manager-reports/file-refs', 
                search: `RefFilesParams=${base64UrlEncode(JSON.stringify(refparams))}` 
              }))
            }
          }
        },
        plotOptions: {
          bar: {
            columnWidth: '45%',
            distributed: true,
            dataLabels: { position: 'top' },
          }
        },
        dataLabels: { enabled: true,
          offsetY: -20,
          style: { fontSize: '12px', colors: [theme.palette.text.secondary] }, 
          formatter: commFormatter,
        },
        legend: { show: false },
        xaxis: { categories, },
        yaxis: { labels: { formatter: (val: number) => val < 1 && val !== 0 ? '' : commFormatter(val) } },
        theme: { mode: theme.palette.mode }
      },
    }
    return sndClssChartOptions
  }, [dispatch, clientsInfo, states.classificationSelection, states.sndClssMap, tStates.end, tStates.kind, tStates.start, theme.palette.mode, theme.palette.text.secondary])

  const filesByDayChartOptions = useMemo(() => {
    if (!statisDto) { return null }
    const tStart = new Date(statisDto.startEnd?.item1!)
    const tEnd = new Date(statisDto.startEnd?.item2!)
    const dates = eachDayOfInterval({ start: tStart, end: tEnd }).map(d => d.toDateString() + ' GMT')
    let displayedDates = dates
    let dataPoints = statisDto.fileCountsByDay ?? []
    const showByWeek = dates.length > 32
    if (showByWeek) {
      const iSunday = dates.findIndex(dStr => {
        const d = new Date(dStr)
        return d.getDay() === 1 // find the first Sunday
      })
      const fileCountsByDay = statisDto.fileCountsByDay ?? []
      const {pointLabels, countsByWeek} = dates.reduce((acc, d, i) => {
        if (i === acc.ws) {
          acc.pointLabels.push(d)
          acc.countsByWeek.push(fileCountsByDay[i])
        } else {
          const c = acc.countsByWeek.length - 1
          acc.countsByWeek[c] = acc.countsByWeek[c] + fileCountsByDay[i]
        }
        if (i === acc.we) {
          acc.ws = acc.we + 1
          acc.we = acc.we + 7
        }
        return acc
      }, { ws: 0, we: iSunday !== 0 ? iSunday - 1 : 6, pointLabels: [] as string[], countsByWeek: [] as number[]})
      displayedDates = pointLabels
      dataPoints = countsByWeek
    }

    const options = {
      series: [{ name: 'file count', data: dataPoints }],
      options: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false
          },
          events: {
            dataPointSelection: (e: any, c: any, selection: any) => {
              const dt = displayedDates[selection.dataPointIndex]
              const x = new Date(dt).toISOString()
              const refparams = { notBefore: tStates.start, notAfter: tStates.end, kind: 0, 
                trKind: tStates.kind,
                clients: clientsInfo?.selectedClients,
                clientNames: clientsInfo?.selectedClients?.map(x => clientsInfo.clientNameMap[x])
              };
              (refparams as any)[!showByWeek ? 'protectedAtDate' : 'protectedAtWeekOf'] = x
              dispatch(push({ 
                pathname: '/secure-files/data-manager-reports/file-refs', 
                search: `RefFilesParams=${base64UrlEncode(JSON.stringify(refparams))}` 
              }))
            }
          }
        },
        xaxis: {
          type: 'datetime',
          categories: displayedDates,
          labels: { datetimeUtc: false }
        },
        yaxis: { labels: { formatter: (val: number) => val < 1 && val !== 0 ? '' : commFormatter(val) } },
        tooltip: {
          ...(showByWeek && {x: { formatter: (val: number, opts?: any) => 'the week of ' + formatDfn(val, 'dd MMM') }}),
          y: { formatter: commFormatter }
        },
        theme: { mode: theme.palette.mode },
        dataLabels: {
          ...(displayedDates.length > 15 ? { enabled: false } : { formatter: commFormatter, }),
          style: { colors: [theme.palette.mode === 'light' ? darken(theme.palette.info.main, 0.5) : theme.palette.common.white] }
        },
      },
    }
    return {options, showByWeek}
  }, [statisDto, theme.palette.mode, theme.palette.info.main, theme.palette.common.white, tStates.start, tStates.end, tStates.kind, clientsInfo?.selectedClients, clientsInfo?.clientNameMap, dispatch])
  
  if (!statisDto) {
    return (
      <Grid item xs={12} container sx={{ '& .MuiSkeleton-root': { mr: 2, mb: 2 } }}>
        <Grid item xs={9}>
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={180} />
        </Grid>
        <Grid item xs={3}>
          <Skeleton variant="rectangular" height={316} />
        </Grid>
        <Grid item xs={12}>
          <Skeleton variant="rectangular" height={180} />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid item xs={12} container
      sx={{
        '& .chart-segment': {
          bgcolor: theme => theme.palette.background.paper,
          boxShadow: theme => `0 0 8px ${darken(theme.palette.background.default, 0.15)}`,
          '& svg': { bgcolor: 'unset !important' },
        },
      }}
    >
      <Grid item xs={9}>
        <Box sx={{ mr: 2 }}>
          <Box padding={2} className="chart-segment" marginBottom={2}>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: '180px repeat(5, 1fr)',
              '& div': { textAlign: 'center', },
              '& h4': { textAlign: 'center', fontWeight: 300, color: 'success.light', fontSize: '22px', mt: 0.5 },
              '& div.label-box': { minHeight: 40, bgcolor: theme => lighten(theme.palette.primary.main, 0.90), ml: -2, mr: 2 },
              '& div.header-box': { 
                borderBottom: theme => `1px solid ${theme.palette.divider}`, 
                color: theme => emphasize(theme.palette.background.default, 0.52),
                mt: -1.5,
              },
            }}>
              <Stack direction="row" className="label-box" justifyContent="center" alignItems="center"
                sx={{ gridArea: '1 / 1 / span 2', my: -2 }}
              >
                {
                    tStates.kind === PopTimeRangeKind.Hours_24 ? 'Last 24 hours'
                  : tStates.kind === PopTimeRangeKind.Days_7 ? 'Last 7 days'
                  : tStates.kind === PopTimeRangeKind.Days_30 ? 'Last 30 days'
                  : tStates.kind === PopTimeRangeKind.Months_3 ? 'Last 3 months'
                  : tStates.kind === PopTimeRangeKind.Months_6 ? 'Last 6 months'
                  : tStates.kind === PopTimeRangeKind.Years_1 ? 'Last 1 year'
                  : tStates.kind === PopTimeRangeKind.Custom ? 
                    <Box sx={{ transform: 'scale(0.8)', }}>{
                      !tStates.end ? <Typography variant="caption">From {format(tStates.start!, 'Pp')}</Typography>
                    : !tStates.start ? <Typography variant="caption">To {format(tStates.end!, 'Pp')}</Typography>
                    : <>
                      <Typography variant="caption" component="p">From {format(tStates.start, 'Pp')}</Typography>
                      <Typography variant="caption">To {format(tStates.end, 'Pp')}</Typography>
                      </>
                    }</Box>
                  : ''
                }
                <Box sx={{ flex: '0 0 auto', '& button': { minWidth: 'unset', p: 'unset', pr: 1 } }}>
                  {PopTimeRangePicker}
                </Box>
              </Stack>
              <Box className="header-box"><Typography variant="caption">Reports</Typography></Box>
              <Box className="header-box"><Typography variant="caption">Total Files Scanned</Typography></Box>
              <Box className="header-box"><Typography variant="caption">Files Protected</Typography></Box>
              <Box className="header-box"><Typography variant="caption">Files Classified</Typography></Box>
              <Box className="header-box"><Typography variant="caption">Active Clients</Typography></Box>
              
              <Typography variant="h4">
                {commFormatter(statisDto.grandTotal?.summariesCount ?? 0)}
              </Typography>
              <Typography variant="h4">
                {commFormatter(statisDto.grandTotal?.totalFilesCount ?? 0)}
              </Typography>
              <Typography variant="h4">
                {commFormatter(statisDto.grandTotal?.protectedCount ?? 0)}
              </Typography>
              <Typography variant="h4">
                {commFormatter(statisDto.grandTotal?.classifiedCount ?? 0)}
              </Typography>
              <Typography variant="h4">
                {commFormatter(statisDto.activeClientsCount ?? null)}
              </Typography>
            </Box>
          </Box>
          <Stack className="chart-segment" padding={2} direction="row">
            {clssPieChartOptions.series.length === 0
              ? <Box height={232} width="100%">
                  <Typography variant="body1" marginBottom={1} color="text.primary">Classification</Typography>
                  <Typography variant="body2" color="warning.main" textAlign="center" paddingTop={8}>No Data</Typography>
                </Box>
              : <>
                  <Box width={300} sx={{ '& > div': { ml: -1 } }}>
                    <Typography variant="body1" marginBottom={1} color="text.primary">Classification</Typography>
                    <Chart height="200" type="pie" {...(clssPieChartOptions as any)} />
                  </Box>
                  {sndClssChartOptions &&
                    <Box sx={{ flex: '1 1 auto'}}>
                      <Chart height="200" type="bar" {...(sndClssChartOptions as any)} />
                    </Box>}
                </>}
          </Stack>
        </Box>
      </Grid>
      <Grid item xs={3}>
        <Box className="chart-segment" padding={2} paddingBottom={0} height={331}>
          <Box padding={2} marginX={-2} marginTop={-2}>
            <Typography variant="body1" color="text.primary">Document Types</Typography>
          </Box>
          {docTypeChartOptions.series[0].data.length === 0
            ? <Box height={275}>
                <Typography variant="body2" color="warning.main" textAlign="center" paddingTop={8}>No Data</Typography>
              </Box>
            : 
            <Box height={291}>
              <Scrollbar options={{ suppressScrollY: true }}>
                <Chart height={chartHeight} type="bar" {...(docTypeChartOptions as any)} />
              </Scrollbar>
            </Box>
          }
        </Box>
      </Grid>
      <Grid item xs={12} marginTop={2}>
        <Box className="chart-segment" padding={2}>
          <Typography variant="body1" color="text.primary">Protected Files by {filesByDayChartOptions?.showByWeek ? 'week' : 'day'}</Typography>
          {(statisDto.grandTotal?.summariesCount ?? 0) === 0 
            ? <Box height={175} width="100%">
                <Typography variant="body2" color="warning.main" textAlign="center" paddingTop={8}>No Data</Typography>
              </Box>
            : <Chart height="160" type="bar" {...(filesByDayChartOptions?.options as any)} />}
        </Box>
      </Grid>
    </Grid>
  )
}

const commFormatter = (val:number|null) => numeral(val).format('0,0')


type SS = ReturnType<typeof initialS>

const PopClientFilter: FC<{states: SS, setStates: (m:(v:SS)=>SS)=>void, ianaTimeZone: string|null}> = ({states, setStates, ianaTimeZone}) => {
  const options = [...states.table.clients.difference(new Set(states.table.selectedClients))]
    .map(id => ({ key: id, text: states.table.clientNameMap[id] }))
    .sort((a, b) => a.text < b.text ? -1 : 1)
  
  return (
    <PopBaseFilter label="Client" id="client-filter"
      options={options as any}
      onChange={(e, value, reason) => {
        if (reason !== 'selectOption') { return }
        setStates(v => {
          const i = v.table.selectedClients.indexOf(value.key as any)
          if (i < 0) {
            v.table.selectedClients.push(value.key as any)
            return {...v,
              statisId: JSON.stringify({ notBefore: v.notBefore, notAfter: v.notAfter, clients: v.table.selectedClients, ianaTimeZone }),
            }
          }
          return v
        })
      }}
    />
  )
}

interface PassbackRefFilesParams {
  notBefore: string,
  notAfter: string,
  trKind: PopTimeRangeKind,
  clients?: string[],
  clientNames?: string[],
}


